import { can } from '@clim/client-core';
import { COMPLIANCE_LABELS, DOC_EXPIRY_LABELS, DOC_EXPIRY_MODES, type Compliance, type ComplianceStatus, type ContractorDocTypeRecord, type DocExpiryMode } from '@clim/domain';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ComplianceBadge, ContractorDocsPanel } from '../components/ContractorDocs';
import { Empty, ErrorBox, Field, Input, Modal, Select } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';

/** Mes documents (sous-traitant) : état, dépôt, mise à jour. */
export function MyDocumentsPage() {
  return (
    <div>
      <h1>Mes documents professionnels</h1>
      <p className="muted">KBIS, assurance, attestation fluides… Déposez un PDF ou une photo ; l’administrateur les contrôle. Vous êtes prévenu avant chaque expiration.</p>
      <div className="card">
        <ContractorDocsPanel userId="me" self />
      </div>
    </div>
  );
}

/** Fiche d'un sous-traitant (admin / superviseur). */
export function ContractorDetailPage() {
  const { userId } = useParams();
  return (
    <div>
      <p>
        <Link to="/sous-traitants">← Tous les sous-traitants</Link>
      </p>
      <div className="card">
        <ContractorDocsPanel userId={userId!} self={false} />
      </div>
    </div>
  );
}

type Row = { userId: string; name: string; company?: string | null; email: string; compliance: Compliance };
type Filter = 'all' | ComplianceStatus | 'to_review';

/** Tableau admin / superviseur (§14) : À jour / Expire bientôt / Expiré / Manquant. */
export function ContractorsPage() {
  const { me, api } = useReady();
  const online = useOnline();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [typesOpen, setTypesOpen] = useState(false);
  const load = useCallback(() => {
    if (online) api.request<Row[]>('GET', '/v1/contractors').then(setRows, (e) => setError(errorMessage(e)));
  }, [api, online]);
  useEffect(load, [load]);
  if (!online) return <Empty>Le suivi des documents nécessite une connexion.</Empty>;
  const count = (f: Filter) => (rows ?? []).filter((r) => (f === 'all' ? true : f === 'to_review' ? r.compliance.toReview > 0 : r.compliance.status === f)).length;
  const shown = (rows ?? []).filter((r) => (filter === 'all' ? true : filter === 'to_review' ? r.compliance.toReview > 0 : r.compliance.status === filter));
  const typeLabels = rows?.[0]?.compliance.types.map((t) => t.label) ?? [];
  const tabs: [Filter, string][] = [
    ['all', 'Tous'],
    ['ok', COMPLIANCE_LABELS.ok],
    ['expiring', COMPLIANCE_LABELS.expiring],
    ['expired', COMPLIANCE_LABELS.expired],
    ['missing', COMPLIANCE_LABELS.missing],
    ['to_review', 'À contrôler'],
  ];
  return (
    <div>
      <div className="page-head">
        <h1>Documents des sous-traitants</h1>
        {can(me, 'contractor_document.manage') && (
          <button className="btn small" onClick={() => setTypesOpen(true)}>
            Types de documents
          </button>
        )}
      </div>
      <p className="muted">Information et alertes uniquement : un document manquant ou expiré ne bloque aucune fonctionnalité.</p>
      <ErrorBox message={error} />
      <div className="chips" role="tablist">
        {tabs.map(([f, l]) => (
          <button key={f} type="button" role="tab" aria-selected={filter === f} className={`chip${filter === f ? ' on' : ''}`} onClick={() => setFilter(f)}>
            {l} ({count(f)})
          </button>
        ))}
      </div>
      {!rows ? null : !shown.length ? (
        <Empty>Aucun sous-traitant.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Sous-traitant</th>
                <th>État</th>
                {typeLabels.map((l) => (
                  <th key={l} className="small">
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.userId}>
                  <td>
                    <Link to={`/sous-traitants/${r.userId}`}>{r.name}</Link>
                    {r.company && <div className="small">🏢 {r.company}</div>}
                    {r.compliance.toReview > 0 && <div className="muted small">{r.compliance.toReview} à contrôler</div>}
                  </td>
                  <td>
                    <ComplianceBadge status={r.compliance.status} />
                  </td>
                  {r.compliance.types.map((t) => (
                    <td key={t.typeId}>
                      <ComplianceBadge status={t.status} />
                      {t.document?.expiresOn && <div className="muted small">{t.document.expiresOn.split('-').reverse().join('/')}</div>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {typesOpen && <DocTypesDialog onClose={() => (setTypesOpen(false), load())} />}
    </div>
  );
}

function DocTypesDialog({ onClose }: { onClose(): void }) {
  const { api } = useReady();
  const [types, setTypes] = useState<ContractorDocTypeRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [required, setRequired] = useState(true);
  const [expiry, setExpiry] = useState<DocExpiryMode>('required');
  const [warnDays, setWarnDays] = useState('30');
  const load = useCallback(() => void api.request<ContractorDocTypeRecord[]>('GET', '/v1/contractor-doc-types').then(setTypes, (e) => setError(errorMessage(e))), [api]);
  useEffect(load, [load]);
  const patch = (id: string, body: Record<string, unknown>) => api.request('PATCH', `/v1/admin/contractor-doc-types/${id}`, body).then(load, (e) => setError(errorMessage(e)));
  return (
    <Modal title="Types de documents" onClose={onClose}>
      <ErrorBox message={error} />
      <ul className="plain">
        {types.map((t) => (
          <li key={t.id} className={`row legal-row${t.isActive ? '' : ' disabled'}`}>
            <label className="switch">
              <input type="checkbox" checked={t.isActive} onChange={() => void patch(t.id, { isActive: !t.isActive })} aria-label={`Activer ${t.label}`} />
              <span>{t.isActive ? 'Activé' : 'Désactivé'}</span>
            </label>
            <div className="row-main">
              <strong>{t.label}</strong>
              <span className="muted">
                {DOC_EXPIRY_LABELS[t.expiry]} · alerte {t.warnDays} j avant
              </span>
            </div>
            <label className="check small">
              <input type="checkbox" checked={t.required} onChange={() => void patch(t.id, { required: !t.required })} />
              Obligatoire
            </label>
          </li>
        ))}
      </ul>
      <div className="form">
        <h3>Ajouter un type</h3>
        <Field label="Libellé">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <div className="grid2">
          <Field label="Date d’expiration">
            <Select value={expiry} onChange={(e) => setExpiry(e.target.value as DocExpiryMode)}>
              {DOC_EXPIRY_MODES.map((m) => (
                <option key={m} value={m}>
                  {DOC_EXPIRY_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Alerte (jours avant)">
            <Input inputMode="numeric" value={warnDays} onChange={(e) => setWarnDays(e.target.value)} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Obligatoire pour chaque sous-traitant
        </label>
        <div className="actions">
          <button
            className="btn primary"
            disabled={label.trim().length < 2}
            onClick={() =>
              api
                .request('POST', '/v1/admin/contractor-doc-types', { label, required, expiry, warnDays: Number(warnDays) || 0 })
                .then(() => (setLabel(''), load()))
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Ajouter
          </button>
        </div>
      </div>
    </Modal>
  );
}
